import { bindable, customElement, inject, LogManager, NewInstance } from "aurelia-framework";
import { Client } from '../api/client';
import * as _ from "lodash";
import { UnsavedChanges } from "../form/unsaved-changes-prevent";
import { EventAggregator } from 'aurelia-event-aggregator';
import { UrlUtils } from "../utilities/url-utils";
import { WindowUtils } from "../utilities/window-utils";
import { OAuthClient } from "../api/oauth-client";
import { HttpClient } from "aurelia-fetch-client";
import { render, unmountComponentAtNode } from 'react-dom';

import "./wiki.less";
import Confluence from "../confluence/Confluence";

@customElement('sio-wiki')
@inject(Client, NewInstance.of(HttpClient), EventAggregator)
export class Wiki {

    @bindable sio = false;

    path;

    pages = {};
    pagesBackup = [];
    selectedPage = null;
    searchTerm = '';
    searchResults = [];
    expandedCategories = {};
    isConfluencePage = false;
    isWikiPage = false;


    constructor(
        client,
        sioClient,
        ea
    ) {
        this.client = client;
        this.sioClient = sioClient;
        this.ea = ea;
    }

    async bind() {
        const urlParams = new URLSearchParams(window.location.search);
        this.path = urlParams.get('path');

        const currentPath = window.location.pathname;
        this.isConfluencePage = currentPath.includes('/view/instance/help');
        this.isWikiPage = currentPath.includes('/view/wiki/wiki');

        await this.updateView();
    }

    async updateView() {
        this.pages = [];
        this.loading = true;

        let promise = null;
        if (this.isConfluencePage) {
            return
        }

        if (this.sio) {
            promise = this.sioClient.get('https://sio-a1rc.s.io/api/v2/public/wiki/content-page?limit=500').then(
                response => this.extractJsonFromResponse(response).then(response => response.data),
                response => {
                    if (response instanceof DOMException && "AbortError" === response.name) {
                        throw response;
                    }
                    return this.extractJsonFromResponse(response).then(response => {
                        throw response;
                    });
                }
            )
        } else {
            promise = this.client.get('wiki/content-page?limit=500');
        }

        promise.then((data) => {

            console.log('DATA', data);

            this.pages = _.groupBy(data?.items ?? [], 'category');
            this.pagesBackup = data?.items ?? [];
            this.loading = false;

            let selectedPage = UrlUtils.getQueryParamValue(window.location.href, 'page');

            if (selectedPage) {
                this.selectedPage = null;

                for (let item of data?.items ?? []) {
                    if (item.id === selectedPage?.page) {
                        this.selectedPage = item;
                        this.expandCategoryForPage(item);
                    }
                }
            }
        })
    }

    get isSearching() {
        return Boolean(this.searchTerm?.trim());
    }

    extractJsonFromResponse(response) {
        // if (!response.ok) {
        //     return Promise.reject({data: null});
        // }

        return response.text().then(data => {
            try {
                response.data = data.length != 0 ? JSON.parse(data) : null;
            } catch (e) {
                response.data = null;
            }

            return response;
        });
    }

    async attached() {
        if (!this.isConfluencePage) {
            this.subscription = this.ea.subscribe('sio_form_post_submit', response => {

                if ('wiki/content-page' !== response.config.modelId) {
                    return;
                }

                //Force clear cache, because order of subscribers is not fixed, and sometimes clear is not called before
                this.client.removeCache(response.config.modelId);
                this.client.removeCache('model/choice/' + response.config.modelId);

                this.updateView().then(data => {

                });
            });
        }
        if (this.isConfluencePage) {
            const confluenceContainer = document.getElementById('sio-confluence');
            if (!confluenceContainer) {
                return
            }
            render(
                <Confluence
                    endpoints={{
                        contentTree: "https://sio-a1rc.s.io/api/v2/public/confluence/content-tree",
                        search: "https://sio-a1rc.s.io/api/v2/public/confluence/search/",
                        page: "https://sio-a1rc.s.io/api/v2/public/confluence/page/"
                    }} />,
                confluenceContainer
            );
        }
    }

    detached() {
        if (!this.isConfluencePage) {
            this.subscription.dispose();
        }
    }

    selectPage(page) {
        this.selectedPage = page;
        this.expandCategoryForPage(page);

        let newurl = UrlUtils.upsertQueryParamValue(
            window.location.href,
            'page',
            { page: page.id }
        );

        WindowUtils.updateLocationHrefWithoutReloading(newurl);
    }

    toggleCategory(category) {
        const isExpanded = this.expandedCategories[category] !== false;
        this.expandedCategories = {
            ...this.expandedCategories,
            [category]: !isExpanded
        };
    }

    expandCategoryForPage(page) {
        if (!page?.category) {
            return;
        }

        this.expandedCategories = {
            ...this.expandedCategories,
            [page.category]: true
        };
    }

    clearSearch() {
        this.searchTerm = '';
        this.searchResults = [];
        this.pages = _.groupBy(this.pagesBackup ?? [], 'category');
    }

    onFrameLoad() {
    }

    searchPage(event) {
        this.searchTerm = event.target.value;
        const query = this.searchTerm.trim().toLowerCase();

        if (!query) {
            this.searchResults = [];
            this.pages = _.groupBy(this.pagesBackup ?? [], 'category');
            return;
        }

        this.searchResults = this.buildSearchResults(query);
    }

    buildSearchResults(query) {
        const grouped = _.groupBy(this.pagesBackup ?? [], 'category');
        const results = [];

        Object.keys(grouped).forEach((category) => {
            if (category === 'undefined') {
                return;
            }

            const pages = grouped[category] ?? [];
            const categoryMatches = category.toLowerCase().includes(query);

            if (pages.length === 1) {
                const page = pages[0];
                const titleMatches = page.title?.toLowerCase().includes(query);

                if (categoryMatches || titleMatches) {
                    results.push({
                        page,
                        category,
                        isSinglePageCategory: true
                    });
                }
                return;
            }

            pages.forEach((page) => {
                const titleMatches = page.title?.toLowerCase().includes(query);

                if (categoryMatches || titleMatches) {
                    results.push({
                        page,
                        category,
                        isSinglePageCategory: false
                    });
                }
            });
        });

        return results;
    }
}
